import { fetchSiteMap } from '@actions/others';
import H1 from '@base/H1/H1';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Fonts } from '@styles/index';

const UNACADEMY_SITEMAP = 'Unacademy Sitemap';

const Flex = styled.div`
  display: flex;
  align-content: space-between;
  flex-direction: column;
`;

const ContentWrapper = styled(Flex)`
  width: 69.3rem;
  height: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  @media (max-width: 70.95em) {
    width: 100%;
    padding: 32px;
  }
  @media (max-width: 47.95em) {
    padding: 16px;
  }
`;

const CategoryWrapper = styled(Flex)`
  width: 100%;
  margin: 0 auto;
  padding: 1.89rem 0;
  margin-bottom: -1px;
  border-bottom: 1px solid #404040;
  box-sizing: border-box;
`;

const GoalWrapper = styled(Flex)`
  width: 100%;
  padding: 0.95rem 0;
  box-sizing: border-box;
`;

const TopicWrapper = styled(Flex)`
  padding: 0.95rem 0;
  width: 100%;
  align-content: space-between;
  flex-wrap: wrap;
  flex-direction: row;
`;

const TopicContent = styled(Flex)`
  min-width: 16.57rem;
  margin-right: 0.63rem;
  min-height: 6.3rem;
  box-sizing: border-box;
`;

const ConceptWrapper = styled(Flex)`
  margin: 0;
  outline: none;
  text-decoration: none;
  box-sizing: border-box;
  word-break: break-word;
`;

const Heading = styled(H1)`
  font-size: 2.016rem;
  margin: 2.4rem 0;
`;

const StyledAnchor = styled.a`
  color: ${({ theme }) => theme.colors.fontGrey};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  font-size: ${Fonts.P2};
  font-weight: ${Fonts.REGULAR};
  transition: color 200ms ease-in-out;
  &:hover {
    color: ${({ theme, isEducator }) =>
      isEducator ? theme.colors.blue : theme.colors.green};
  }
`;

const CategoryLink = styled.div`
  display: flex;
  align-content: space-between;
  font-size: 1.26rem;
  line-height: 1.764rem;
  text-transform: uppercase;
  font-weight: 500;
`;

const GoalLink = styled.div`
  display: flex;
  align-content: space-between;
  font-size: 1.512rem;
  font-weight: 700;
`;

const TopicLink = styled.div`
  display: flex;
  align-content: space-between;
  font-size: 1.008rem;
  line-height: 1.411rem;
  font-weight: 700;
  margin-bottom: 0.315rem;
  margin-top: 1.26rem;
`;

const ConceptLink = styled.div`
  display: flex;
  align-content: space-between;
  font-size: 0.882rem;
  line-height: 1.2348rem;
`;

const SiteMap = ({ siteMap }) => {
  const renderConcepts = (children) => {
    const concepts = children.map((concept) => {
      if (concept.level !== 30 && concept.has_course) {
        return (
          <ConceptWrapper id={`sitemap_concept_${concept.uid}`}>
            <StyledAnchor
              color="BLACK"
              href={concept.permalink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ConceptLink>{concept.name}</ConceptLink>
            </StyledAnchor>
          </ConceptWrapper>
        );
      }
      return null;
    });
    return concepts;
  };
  const renderTopicGroup = (children) => {
    const topics = children.map((topic) => {
      if (topic.has_course) {
        return (
          <TopicContent column id={`sitemap_topic_group_${topic.uid}`}>
            <StyledAnchor
              color="BLACK"
              href={topic.permalink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <TopicLink>{topic.name}</TopicLink>
            </StyledAnchor>
            {topic.children.length ? renderConcepts(topic.children) : null}
          </TopicContent>
        );
      }
      return null;
    });
    return topics;
  };
  const renderTopics = (children) => {
    const topics = children.map((topic) => {
      if (topic.level !== 30) {
        if (topic.has_course) {
          return (
            <TopicContent column id={`sitemap_topic_${topic.uid}`}>
              <StyledAnchor
                color="BLACK"
                href={topic.permalink}
                rel="noopener noreferrer"
                target="_blank"
              >
                <TopicLink>{topic.name}</TopicLink>
              </StyledAnchor>
              {topic.children.length ? renderConcepts(topic.children) : null}
            </TopicContent>
          );
        }
        return null;
      }
      return renderTopicGroup(topic.children);
    });
    return topics;
  };
  const renderGoalGroup = (children) => {
    const goals = children.map((goal) => {
      if (goal.has_course) {
        return (
          <GoalWrapper column id={`sitemap_goal_children_${goal.uid}`}>
            <StyledAnchor
              color="BLACK"
              href={goal.permalink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <GoalLink>{goal.name}</GoalLink>
            </StyledAnchor>
            <TopicWrapper>
              {goal.children.length ? renderTopics(goal.children) : null}
            </TopicWrapper>
          </GoalWrapper>
        );
      }
      return null;
    });
    return goals;
  };
  const renderGoals = (children) => {
    const goals = children.map((goal) => {
      if (goal.level !== 30) {
        if (goal.has_course) {
          return (
            <GoalWrapper column id={`sitemap_goal_${goal.uid}`}>
              <StyledAnchor
                color="BLACK"
                href={goal.permalink}
                rel="noopener noreferrer"
                target="_blank"
              >
                {goal.name}
              </StyledAnchor>
              <TopicWrapper>
                {goal.children.length ? renderTopics(goal.children) : null}
              </TopicWrapper>
            </GoalWrapper>
          );
        }
        return null;
      }
      return renderGoalGroup(goal.children);
    });
    return goals;
  };
  const renderCategories = () => {
    const categories = siteMap.map((categoryData, idx) => {
      if (categoryData.has_course) {
        return (
          <CategoryWrapper column id={`sitemap_category_${idx}`}>
            <StyledAnchor
              color="BLACK"
              href={categoryData.permalink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <CategoryLink>{categoryData.name}</CategoryLink>
            </StyledAnchor>
            {categoryData.children.length
              ? renderGoals(categoryData.children)
              : null}
          </CategoryWrapper>
        );
      }
      return null;
    });
    return categories;
  };
  const renderSiteMap = () => {
    return renderCategories();
  };

  return (
    <ContentWrapper>
      <Heading>{UNACADEMY_SITEMAP}</Heading>
      {renderSiteMap()}
    </ContentWrapper>
  );
};

const Layout = ({ children }) => children;

SiteMap.Layout = Layout;

SiteMap.getInitialProps = async ({ store }) => {
  const rawResponse = await store.dispatch(fetchSiteMap());
  return { siteMap: rawResponse.result.results };
};

SiteMap.propTypes = {
  siteMap: PropTypes.arrayOf(PropTypes.Object).isRequired
};

SiteMap.pageInfo = {
  name: 'Unacademy Sitemap'
};

export default SiteMap;
